import React, { useState } from "react"

const BuildingStats = ({ buildingData }) => {
  const [activeTab, setActiveTab] = useState("overview")

  if (!buildingData) {
    return <div className="p-6 text-sm text-gray-500">No building data loaded.</div>
  }

  const jobs = buildingData.jobs || []
  const serviceCalls = buildingData.serviceCalls || []
  const tenants = buildingData.tenants || []
  const maintenanceContracts = buildingData.maintenanceContracts || []
  const transmitterContracts = buildingData.transmitterContracts || []
  const hoursWorked = buildingData.hoursWorked || []
  const proposals = buildingData.proposals || []

  const latestHours = hoursWorked[0] || { total: 0, overtime: 0 }

// --- Build unified recent activity timeline (Version C) ---

// Helper: turn various date strings into a comparable timestamp
    const parseToTimestamp = (value) => {
      if (!value) return 0
      // handle "YYYY-MM-DD" and "YYYY-MM-DD HH:MM:SS" and date-like strings
      const d = new Date(value)
      const ts = d.getTime()
      return Number.isNaN(ts) ? 0 : ts
    }

    let timelineEvents = []

    // 1) Jobs (projects)
    jobs.forEach((j) => {
      const ts = parseToTimestamp(j.date)
      if (!ts) return
      timelineEvents.push({
        ts,
        type: "job",
        icon: "📁",
        bg: "bg-blue-50",
        title: j.status === "Completed" ? "Job completed" : "Job updated",
        desc: `${j.title || "Project"} — ${j.status || ""}`,
      })
    })

    // 2) Service calls
    serviceCalls.forEach((s) => {
      const ts = parseToTimestamp(s.date)
      if (!ts) return
      const resolved = (s.status || "").toLowerCase() === "resolved" || (s.status || "").toLowerCase() === "completed"
      timelineEvents.push({
        ts,
        type: "service",
        icon: "📞",
        bg: "bg-green-50",
        title: resolved ? "Service call resolved" : "Service call logged",
        desc: `${s.issue || "Service call"} — ${s.type || ""}`,
      })
    })

    // 3) Invoices
    ;(buildingData.invoices || []).forEach((inv) => {
      const ts = parseToTimestamp(inv.date)
      if (!ts) return
      const isPaid = (inv.status || "").toLowerCase() === "paid"
      timelineEvents.push({
        ts,
        type: "invoice",
        icon: "💲",
        bg: "bg-amber-50",
        title: isPaid ? "Invoice paid" : "Invoice issued",
        desc: `${inv.number || "Invoice"} — $${inv.total || 0}`,
      })
    })

    // 4) Inspections (optional but nice)
    ;(buildingData.inspections || []).forEach((ins) => {
      const ts =
        parseToTimestamp(ins.actual_inspection_date) ||
        parseToTimestamp(ins.scheduled_due_date)
      if (!ts) return
      timelineEvents.push({
        ts,
        type: "inspection",
        icon: "✅",
        bg: "bg-purple-50",
        title: "Inspection",
        desc: `${ins.status || "Completed"} — ${
          ins.actual_inspection_date || ins.scheduled_due_date || ""
        }`,
      })
    })

    // Sort by most recent timestamp (desc) and take top 3
    timelineEvents.sort((a, b) => b.ts - a.ts)
    const recentActivities = timelineEvents.slice(0, 3)


  const tabs = [
    { value: "overview", label: "Overview" },
    { value: "jobs", label: "Jobs" },
    { value: "service", label: "Service" },
    { value: "contracts", label: "Contracts" },
    { value: "tenants", label: "Tenants" },
    { value: "transmitters", label: "Transmitters" },
    { value: "hours", label: "Hours" },
    { value: "proposals", label: "Proposals" },
  ]

//  // Build 3 recent activities dynamically
//   const activities = []

//   // Most recent job
//   if (buildingData.jobs && buildingData.jobs.length > 0) {
//     const j = buildingData.jobs[0]
//     activities.push({
//       icon: "📁",
//       bg: "bg-blue-50",
//       title: "New Job",
//       desc: `${j.title} — ${j.status}`,
//     })
//   }

//   // Most recent service call
//   if (buildingData.serviceCalls && buildingData.serviceCalls.length > 0) {
//     const s = buildingData.serviceCalls[0]
//     activities.push({
//       icon: "📞",
//       bg: "bg-green-50",
//       title: s.status === "Resolved" ? "Service Call Resolved" : "Service Call Logged",
//       desc: `${s.issue} — ${s.type}`,
//     })
//   }

//   // Most recent invoice (if exists)
//   if (buildingData.invoices && buildingData.invoices.length > 0) {
//     const inv = buildingData.invoices[0]
//     activities.push({
//       icon: "💲",
//       bg: "bg-amber-50",
//       title: inv.status === "Paid" ? "Invoice Paid" : "Invoice Issued",
//       desc: `${inv.number} — $${inv.total}`,
//     })
//   }

  // Limit to 3
 

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
              {buildingData.address || "Building Address"}
            </h1>
            <p className="mt-2 text-sm text-gray-500">Complete building management overview</p>
          </div>
          <span className="inline-flex items-center self-start rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
            <span className="mr-2 inline-block h-2 w-2 rounded-full bg-emerald-500" />
            Active Property
          </span>
        </div>

        {/* Stats Overview */}
        <div className="mb-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Active Jobs */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Active Jobs
              </span>
              <span className="text-xs text-gray-400">📁</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{jobs.length}</div>
            <p className="mt-1 text-xs text-gray-500">
              {jobs.filter((j) => j.status === "In Progress").length} in progress
            </p>
          </div>

          {/* Service Calls */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Service Calls
              </span>
              <span className="text-xs text-gray-400">📞</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{serviceCalls.length}</div>
            <p className="mt-1 text-xs text-gray-500">
              {serviceCalls.filter((s) => s.status === "Scheduled").length} scheduled
            </p>
          </div>

          {/* Tenants */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Total Tenants
              </span>
              <span className="text-xs text-gray-400">👥</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{tenants.length}</div>
            <p className="mt-1 text-xs text-gray-500">Across multiple floors</p>
          </div>

          {/* Hours */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Hours (Latest Month)
              </span>
              <span className="text-xs text-gray-400">⏱️</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{latestHours.total || 0}</div>
            <p className="mt-1 text-xs text-gray-500">
              {latestHours.overtime || 0} overtime hours
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="space-y-6">
          {/* Tab List */}
          <div className="mb-2 flex flex-wrap gap-2 border-b border-gray-200 pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`rounded-t-md px-3 py-1.5 text-xs font-medium md:text-sm ${
                  activeTab === tab.value
                    ? "border border-b-white border-gray-300 bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* OVERVIEW */}
          {activeTab === "overview" && (
            <div className="grid gap-4 md:grid-cols-2">
              {/* Recent Activity */}
              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-2">
                  <h2 className="text-sm font-semibold text-gray-900">Recent Activity</h2>
                  <p className="text-xs text-gray-500">
                    Latest updates across all categories
                  </p>
                </div>
                <div className="space-y-4">
                  {recentActivities.map((a, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full ${a.bg} text-xs`}
                      >
                        {a.icon}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{a.title}</p>
                        <p className="text-xs text-gray-500">{a.desc}</p>
                      </div>
                    </div>
                  ))}

                  {recentActivities.length === 0 && (
                    <p className="text-xs text-gray-500">
                      No recent activity available for this building.
                    </p>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-2">
                  <h2 className="text-sm font-semibold text-gray-900">Quick Stats</h2>
                  <p className="text-xs text-gray-500">
                    Building metrics at a glance
                  </p>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Active Contracts</span>
                    <span className="font-semibold text-gray-900">
                      {maintenanceContracts.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">CO Transmitters</span>
                    <span className="font-semibold text-gray-900">
                      {transmitterContracts.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Open Proposals</span>
                    <span className="font-semibold text-gray-900">
                      {proposals.filter((p) => p.status !== "Approved").length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Total Jobs This Month</span>
                    <span className="font-semibold text-gray-900">{jobs.length}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* JOBS */}
          {activeTab === "jobs" && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-2">
                <h2 className="text-sm font-semibold text-gray-900">All Jobs</h2>
                <p className="text-xs text-gray-500">
                  Complete list of jobs for this building
                </p>
              </div>
              <div className="space-y-3">
                {jobs.map((job) => (
                  <div
                    key={job.id}
                    className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">{job.title}</p>
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            job.priority === "High"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {job.priority}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {job.id} • {job.date}
                      </p>
                    </div>
                    <span
                      className={`inline-flex self-start rounded-full px-2 py-0.5 text-xs font-medium ${
                        job.status === "Completed"
                          ? "bg-green-100 text-green-700"
                          : "bg-white text-gray-700 border border-gray-300"
                      }`}
                    >
                      {job.status}
                    </span>
                  </div>
                ))}
                {jobs.length === 0 && (
                  <p className="text-xs text-gray-500">No jobs found for this building.</p>
                )}
              </div>
            </div>
          )}

          {/* SERVICE */}
          {activeTab === "service" && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-2">
                <h2 className="text-sm font-semibold text-gray-900">Service Calls</h2>
                <p className="text-xs text-gray-500">
                  All service requests for this building
                </p>
              </div>
              <div className="space-y-3">
                {serviceCalls.map((call) => (
                  <div
                    key={call.id}
                    className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">{call.issue}</p>
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            call.type === "Emergency"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {call.type}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {call.id} • {call.date}
                      </p>
                    </div>
                    <span
                      className={`inline-flex self-start rounded-full px-2 py-0.5 text-xs font-medium ${
                        call.status === "Resolved"
                          ? "bg-green-100 text-green-700"
                          : "bg-white text-gray-700 border border-gray-300"
                      }`}
                    >
                      {call.status}
                    </span>
                  </div>
                ))}
                {serviceCalls.length === 0 && (
                  <p className="text-xs text-gray-500">
                    No service calls found for this building.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* CONTRACTS */}
          {activeTab === "contracts" && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-2">
                <h2 className="text-sm font-semibold text-gray-900">
                  Maintenance Contracts
                </h2>
                <p className="text-xs text-gray-500">Active maintenance agreements</p>
              </div>
              <div className="space-y-3">
                {maintenanceContracts.map((contract) => (
                  <div
                    key={contract.id}
                    className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{contract.vendor}</p>
                      <p className="text-xs text-gray-500">
                        {contract.service} • Expires: {contract.expires}
                      </p>
                    </div>
                    <span className="inline-flex self-start rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                      {contract.status}
                    </span>
                  </div>
                ))}
                {maintenanceContracts.length === 0 && (
                  <p className="text-xs text-gray-500">
                    No maintenance contracts for this building.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* TENANTS */}
          {activeTab === "tenants" && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-2">
                <h2 className="text-sm font-semibold text-gray-900">Building Tenants</h2>
                <p className="text-xs text-gray-500">All tenants in this building</p>
              </div>
              <div className="space-y-3">
                {tenants.map((tenant) => (
                  <div
                    key={tenant.id}
                    className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{tenant.name}</p>
                      <p className="text-xs text-gray-500">
                        {tenant.unit} • Lease until: {tenant.lease}
                      </p>
                      <p className="text-xs text-gray-400">{tenant.contact}</p>
                    </div>
                    <button className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                      View Details
                    </button>
                  </div>
                ))}
                {tenants.length === 0 && (
                  <p className="text-xs text-gray-500">No tenants found for this building.</p>
                )}
              </div>
            </div>
          )}

          {/* TRANSMITTERS */}
          {activeTab === "transmitters" && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-2">
                <h2 className="text-sm font-semibold text-gray-900">
                  Central Office Transmitter Contracts
                </h2>
                <p className="text-xs text-gray-500">
                  Communication infrastructure agreements
                </p>
              </div>
              <div className="space-y-3">
                {transmitterContracts.map((contract) => (
                  <div
                    key={contract.id}
                    className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-sm">
                        📡
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {contract.provider}
                        </p>
                        <p className="text-xs text-gray-500">
                          {contract.type} • Expires: {contract.expires}
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex self-start rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                      {contract.status}
                    </span>
                  </div>
                ))}
                {transmitterContracts.length === 0 && (
                  <p className="text-xs text-gray-500">
                    No transmitter contracts for this building.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* HOURS */}
          {activeTab === "hours" && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-2">
                <h2 className="text-sm font-semibold text-gray-900">Hours Worked</h2>
                <p className="text-xs text-gray-500">
                  Labor hours tracked for this building
                </p>
              </div>
              <div className="space-y-3">
                {hoursWorked.map((record, index) => (
                  <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">{record.month}</p>
                      <span className="inline-flex rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs font-medium text-gray-700">
                        {record.total} hours
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-gray-500">Regular Hours</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {record.regular}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Overtime Hours</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {record.overtime}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                {hoursWorked.length === 0 && (
                  <p className="text-xs text-gray-500">
                    No hours logged for this building yet.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* PROPOSALS */}
          {activeTab === "proposals" && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-2">
                <h2 className="text-sm font-semibold text-gray-900">Proposals</h2>
                <p className="text-xs text-gray-500">
                  Project proposals for this building
                </p>
              </div>
              <div className="space-y-3">
                {proposals.map((proposal) => (
                  <div
                    key={proposal.id}
                    className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {proposal.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        {proposal.id} • Submitted: {proposal.date}
                      </p>
                      <p className="mt-1 text-lg font-semibold text-blue-600">
                        {proposal.amount}
                      </p>
                    </div>
                    <span
                      className={`inline-flex self-start rounded-full px-2 py-0.5 text-xs font-medium ${
                        proposal.status === "Approved"
                          ? "bg-green-100 text-green-700"
                          : proposal.status === "Under Review"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-white text-gray-700 border border-gray-300"
                      }`}
                    >
                      {proposal.status}
                    </span>
                  </div>
                ))}
                {proposals.length === 0 && (
                  <p className="text-xs text-gray-500">
                    No proposals found for this building.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
export default BuildingStats;